import {
  APP_DATA,
  EMOJI_REACTION_TYPE,
  isAndroid,
  isIOS,
  isIPadOS,
} from "../common/constants";
import { Box, Flex } from "@100mslive/react-ui";
import {
  HMSRoomState,
  selectAppData,
  selectIsConnectedToRoom,
  selectPeers,
  selectRoomState,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import React, { useContext, useEffect, useRef, useState } from "react";

import { ConferenceMainView } from "../layouts/mainView";
import { Footer } from "./Footer";
import FullPageProgress from "./FullPageProgress";
import { Header } from "./Header";
import { LiveHodoContext } from "../contexts/LiveHodoContext";
import { ROLES } from "../common/roles";
import { RoleChangeRequestModal } from "./RoleChangeRequestModal";
import { useIsHeadless } from "./AppData/useUISettings";
import { useNavigation } from "./hooks/useNavigation";
import { useParams } from "react-router-dom";
import { usePrevious } from "react-use";

const CabinConference = () => {
  const { setCabins } = useContext(LiveHodoContext);

  const navigate = useNavigation();
  const { roomId, role } = useParams();
  const isHeadless = useIsHeadless();
  const roomState = useHMSStore(selectRoomState);
  const prevState = usePrevious(roomState);
  const isConnectedToRoom = useHMSStore(selectIsConnectedToRoom);
  const hmsActions = useHMSActions();
  const [hideControls, setHideControls] = useState(false);
  const dropdownList = useHMSStore(selectAppData(APP_DATA.dropdownList));
  const headerRef = useRef();
  const footerRef = useRef();
  const dropdownListRef = useRef();
  const performAutoHide = hideControls && (isAndroid || isIOS || isIPadOS);

  const peers = useHMSStore(selectPeers);

  useEffect(() => {
    const peersRaw = peers?.filter(
      peer =>
        peer.roleName === ROLES.HOST ||
        peer.roleName === ROLES.CABIN ||
        peer.roleName === ROLES.PRESENTER ||
        peer.roleName === ROLES.PANELIST ||
        peer.roleName === ROLES.TRANSLATOR ||
        peer.roleName === ROLES.E_V ||
        peer.roleName === ROLES.F_V ||
        peer.roleName === ROLES.VIETNAMESE
    );

    setCabins(peersRaw);
  }, [peers]);

  const toggleControls = e => {
    if (dropdownListRef.current?.length === 0) {
      setHideControls(value => !value);
    }
  };

  useEffect(() => {
    let timeout = null;
    dropdownListRef.current = dropdownList || [];
    if (dropdownListRef.current.length === 0) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (dropdownListRef.current.length === 0) {
          setHideControls(true);
        }
      }, 5000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [dropdownList, hideControls]);

  useEffect(() => {
    if (!roomId) {
      navigate(`/`);
      return;
    }
    if (
      !prevState &&
      !(
        roomState === HMSRoomState.Connecting ||
        roomState === HMSRoomState.Reconnecting ||
        isConnectedToRoom
      )
    ) {
      if (role) navigate(`/preview/${roomId || ""}/${role}`);
      else navigate(`/preview/${roomId || ""}`);
    }
  }, [isConnectedToRoom, prevState, roomState, navigate, role, roomId]);

  useEffect(() => {
    // beam doesn't need to store messages, saves on unnecessary store updates in large calls
    if (isHeadless) {
      hmsActions.ignoreMessageTypes(["chat", EMOJI_REACTION_TYPE]);
    }
  }, [isHeadless, hmsActions]);

  if (!isConnectedToRoom) {
    return <FullPageProgress />;
  }

  return (
    <Flex css={{ size: "100%", overflow: "hidden" }} direction="column">
      {!isHeadless && (
        <Box
          ref={headerRef}
          css={{
            h: "$18",
            transition: "margin 0.3s ease-in-out",
            marginTop: performAutoHide
              ? `-${headerRef.current?.clientHeight}px`
              : "none",
            "@md": {
              h: "$32",
            },
          }}
          data-testid="header"
        >
          <Header />
        </Box>
      )}

      <Box
        css={{
          w: "100%",
          flex: "1 1 0",
          minHeight: 0,
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        id="cabin-conferencing"
        data-testid="cabin-conferencing"
        onClick={toggleControls}
      >
        <ConferenceMainView isCabin />
      </Box>

      {!isHeadless && (
        <Box
          ref={footerRef}
          css={{
            flexShrink: 0,
            maxHeight: "$24",
            transition: "margin 0.3s ease-in-out",
            marginBottom: performAutoHide
              ? `-${footerRef.current?.clientHeight}px`
              : undefined,
            "@md": {
              maxHeight: "unset",
            },
          }}
          data-testid="footer"
        >
          <Footer />
        </Box>
      )}
      <RoleChangeRequestModal />
    </Flex>
  );
};

export default CabinConference;
